import React from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { useRequireSignIn } from './useRequireSignIn';
import { useAuthStore } from '../../store/authStore';

interface Props {
  /** What the visitor would see here once signed in, e.g. "your gallery". */
  what: string;
  /** One line explaining why an account is needed. */
  detail?: string;
}

/**
 * Signed-out state for surfaces that only ever show a user's own data.
 *
 * Those pages are reachable without an account now that browsing is open, and
 * their queries answer 401. Rendering the ordinary empty state there would tell
 * a visitor they have no work, which isn't true — they have no session. This
 * says so and offers the way in.
 *
 * Callers render this on `!isAuthenticated`, which is false both for a visitor
 * with no session and for a signed-in user whose checkAuth() has not resolved
 * yet. Waiting here rather than in each caller means no page can tell a
 * signed-in user to sign in during that window — the bug this panel caused on
 * /settings/keys, where the prompt it offered was for a session already held.
 */
export const SignedOutPanel: React.FC<Props> = ({ what, detail }) => {
  const { promptSignIn } = useRequireSignIn();
  const isResolving = useAuthStore(state => state.isLoading);

  if (isResolving) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320, px: 3, py: 6 }}>
        <CircularProgress size={28} aria-label="Checking your session" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'center',
        minHeight: 320,
        px: 3,
        py: 6,
      }}
    >
      <Stack spacing={1.5} alignItems="center" sx={{ maxWidth: 420, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'action.hover',
            color: 'text.secondary',
          }}
        >
          <LockOutlinedIcon />
        </Box>
        <Typography variant="h6" fontWeight={600}>
          Sign in to see {what}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {detail ?? 'Your work is tied to your account. Exploring the labs stays open either way.'}
        </Typography>
        <Button
          variant="contained"
          onClick={promptSignIn}
          sx={{ mt: 1, textTransform: 'none', borderRadius: 999, px: 3 }}
        >
          Sign in
        </Button>
      </Stack>
    </Box>
  );
};
